import type {
  ShortLinkClickRecord,
  ShortLinkDomain,
  ShortLinkListEntry,
  ShortLinkMetricResult,
  ShortLinkQueryParams,
  ShortLinkTagUsage,
  ShortLinkTimeSeriesResult,
} from "@/shortLinks/types";

type Granularity = "hourly" | "daily" | "weekly" | "monthly";

// Deterministic PRNG (Lehmer/park-miller) so preview numbers stay stable across
// renders/reloads instead of jittering on every fetch.
function seeded(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

function pick<T>(arr: T[], r: number): T {
  return arr[Math.min(arr.length - 1, Math.floor(r * arr.length))];
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

// Captured once at module load so generated "recent" timestamps stay stable
// within a session. Using Date.now() per call makes otherwise-seeded data
// non-deterministic across calls, which flakes equality assertions in CI.
const PREVIEW_NOW = Date.now();

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

function daysFromNow(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
}

const DOMAINS: ShortLinkDomain[] = [
  {
    id: 1,
    domain: "mrdn.link",
    isDefault: true,
    isShared: false,
    redirectBaseUrl: "https://meridianhealth.example",
    verificationStatus: "verified",
  },
  {
    id: 2,
    domain: "go.meridianhealth.example",
    isDefault: false,
    isShared: false,
    redirectBaseUrl: "https://meridianhealth.example",
    verificationStatus: "verified",
  },
  {
    id: 3,
    domain: "sh.rt",
    isDefault: false,
    isShared: true,
    redirectBaseUrl: "https://short.example",
    verificationStatus: "pending",
  },
];

const TAGS: ShortLinkTagUsage[] = [
  { id: 1, name: "campaign", assignments: 6 },
  { id: 2, name: "email", assignments: 4 },
  { id: 3, name: "social", assignments: 5 },
  { id: 4, name: "patient-education", assignments: 3 },
  { id: 5, name: "appointments", assignments: 2 },
];

function domainName(domainId: number): string {
  return DOMAINS.find((d) => d.id === domainId)?.domain ?? "mrdn.link";
}

type RawLink = {
  id: number;
  domainId: number;
  shortCode: string;
  longUrl: string;
  title: string;
  tags: string[];
  clicks: number;
  isDisabled?: boolean;
  expiresInDays?: number;
  maxVisits?: number;
};

const RAW_LINKS: RawLink[] = [
  {
    id: 1,
    domainId: 1,
    shortCode: "bookvisit",
    longUrl: "https://meridianhealth.example/appointments/book",
    title: "Book a visit",
    tags: ["campaign", "appointments"],
    clicks: 1240,
  },
  {
    id: 2,
    domainId: 1,
    shortCode: "insurance",
    longUrl: "https://meridianhealth.example/insurance/verify",
    title: "Verify your insurance",
    tags: ["patient-education"],
    clicks: 862,
  },
  {
    id: 3,
    domainId: 2,
    shortCode: "flushot",
    longUrl: "https://meridianhealth.example/flu-shot-clinic",
    title: "Flu shot clinic",
    tags: ["email", "campaign"],
    clicks: 544,
  },
  {
    id: 4,
    domainId: 1,
    shortCode: "portal",
    longUrl: "https://meridianhealth.example/patient-portal",
    title: "Patient portal sign-in",
    tags: ["patient-education"],
    clicks: 410,
  },
  {
    id: 5,
    domainId: 3,
    shortCode: "webinar",
    longUrl: "https://meridianhealth.example/events/heart-health-webinar",
    title: "Heart health webinar",
    tags: ["email", "social"],
    clicks: 318,
    expiresInDays: 14,
    maxVisits: 500,
  },
  {
    id: 6,
    domainId: 2,
    shortCode: "newpatient",
    longUrl: "https://meridianhealth.example/new-patient",
    title: "New patient welcome",
    tags: ["campaign", "email"],
    clicks: 207,
  },
  {
    id: 7,
    domainId: 1,
    shortCode: "locations",
    longUrl: "https://meridianhealth.example/locations",
    title: "Clinic locations",
    tags: ["social"],
    clicks: 96,
  },
  {
    id: 8,
    domainId: 3,
    shortCode: "old-promo",
    longUrl: "https://meridianhealth.example/spring-checkup",
    title: "Spring checkup (ended)",
    tags: ["campaign"],
    clicks: 41,
    isDisabled: true,
    expiresInDays: -5,
  },
];

const SHORT_LINKS: ShortLinkListEntry[] = RAW_LINKS.map((l, i) => ({
  id: l.id,
  shortLinkDomainId: l.domainId,
  shortCode: l.shortCode,
  longUrl: l.longUrl,
  title: l.title,
  createdBy: "preview@freshpaint.io",
  expiresAt:
    l.expiresInDays != null ? daysFromNow(l.expiresInDays).toISOString() : null,
  maxVisits: l.maxVisits ?? null,
  isDisabled: l.isDisabled ?? false,
  usePermanentRedirect: i % 2 === 0,
  forwardQueryParams: i % 3 !== 0,
  createdAt: daysAgo(90 - i * 7).toISOString(),
  updatedAt: daysAgo(i).toISOString(),
  domainName: domainName(l.domainId),
  tags: l.tags,
  clicks: l.clicks,
}));

const METRIC_VALUES: Record<string, string[]> = {
  country: ["United States", "Canada", "United Kingdom", "Germany", "Ireland"],
  region: ["Illinois", "Wisconsin", "Indiana", "Ohio", "Michigan"],
  city: ["Chicago", "Madison", "Indianapolis", "Milwaukee", "Springfield"],
  county: ["Cook County", "Dane County", "Marion County", "Milwaukee County"],
  zip: ["60601", "53703", "46204", "53202", "62701"],
  browser: ["Chrome", "Safari", "Firefox", "Edge", "Opera"],
  os: ["macOS", "Windows", "iOS", "Android", "Linux"],
  device: ["Desktop", "Mobile", "Tablet"],
  link_click_type: ["Link", "QR code"],
  utm_source: ["google", "facebook", "newsletter", "twitter", "linkedin"],
  utm_medium: ["cpc", "email", "social", "referral", "organic"],
  utm_campaign: ["flu_clinic", "heart_health", "new_patient", "retargeting"],
  utm_term: ["appointments", "insurance", "portal", "flu shot"],
  utm_content: ["hero_cta", "footer_link", "sidebar", "banner"],
  short_code: ["bookvisit", "insurance", "flushot", "portal", "webinar"],
  domain: ["mrdn.link", "go.meridianhealth.example", "sh.rt"],
  long_url: [
    "https://meridianhealth.example/appointments/book",
    "https://meridianhealth.example/insurance/verify",
    "https://meridianhealth.example/patient-portal",
  ],
};

const GEOS: { code: string; region: string; city: string }[] = [
  { code: "US", region: "Illinois", city: "Chicago" },
  { code: "US", region: "Wisconsin", city: "Madison" },
  { code: "US", region: "Indiana", city: "Indianapolis" },
  { code: "US", region: "Wisconsin", city: "Milwaukee" },
  { code: "US", region: "Illinois", city: "Springfield" },
];

// Minimal opaque 1x1 black PNG. Scaled up in the QR dialog it renders as a solid
// placeholder tile — enough to exercise the preview/download UI without a real
// QR encoder. Must be a byte-valid PNG (correct chunk CRCs): the PDF export path
// decodes it with jsPDF, which rejects malformed PNGs that browsers tolerate.
const QR_PNG_BASE64 =
  "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4XmNgYGD4DwABBAEA43qFxAAAAABJRU5ErkJggg==";

// Minimal placeholder SVG mirroring the PNG mock: a solid tile that's enough to
// exercise the SVG download path without a real QR encoder.
const QR_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 1 1" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#000000"/></svg>';

function enumerateBuckets(
  from: Date,
  to: Date,
  granularity: Granularity,
): Date[] {
  const out: Date[] = [];
  const cursor = new Date(from);
  // Guard against pathological ranges producing an unbounded loop.
  while (cursor <= to && out.length < 2000) {
    out.push(new Date(cursor));
    if (granularity === "hourly") cursor.setHours(cursor.getHours() + 1);
    else if (granularity === "daily") cursor.setDate(cursor.getDate() + 1);
    else if (granularity === "weekly") cursor.setDate(cursor.getDate() + 7);
    else cursor.setMonth(cursor.getMonth() + 1);
  }
  return out;
}

export function previewShortLinks(): ShortLinkListEntry[] {
  return clone(SHORT_LINKS);
}

export function previewShortLink(shortLinkId: number): ShortLinkListEntry {
  return clone(SHORT_LINKS.find((l) => l.id === shortLinkId) ?? SHORT_LINKS[0]);
}

export function previewShortLinkTags(): ShortLinkTagUsage[] {
  return clone(TAGS);
}

export function previewShortLinkDomains(): ShortLinkDomain[] {
  return clone(DOMAINS);
}

export function previewShortLinkClicks(
  shortLinkId: number,
): ShortLinkClickRecord[] {
  const rng = seeded(shortLinkId * 977 + 17);
  const count = 28 + Math.floor(rng() * 28);
  const records: ShortLinkClickRecord[] = [];
  for (let i = 0; i < count; i++) {
    const geo = pick(GEOS, rng());
    const timestamp = new Date(
      PREVIEW_NOW - Math.floor(rng() * 30 * 24 * 60 * 60 * 1000),
    );
    records.push({
      timestamp: timestamp.toISOString(),
      clickType: pick(["link", "qr"], rng()),
      countryCode: geo.code,
      region: geo.region,
      city: geo.city,
      browserName: pick(["Chrome", "Safari", "Firefox", "Edge"], rng()),
      osName: pick(["macOS", "Windows", "iOS", "Android"], rng()),
      deviceType: pick(["Desktop", "Mobile", "Tablet"], rng()),
      utmSource: pick(
        ["google", "facebook", "newsletter", "twitter", ""],
        rng(),
      ),
      utmMedium: pick(["cpc", "email", "social", "referral", ""], rng()),
      utmCampaign: pick(
        ["flu_clinic", "heart_health", "new_patient", ""],
        rng(),
      ),
      isBot: rng() < 0.05,
    });
  }
  return records;
}

export function previewShortLinkTimeSeries(
  params: ShortLinkQueryParams,
  granularity: Granularity,
): ShortLinkTimeSeriesResult {
  const from = params.fromDate ? new Date(params.fromDate) : daysAgo(30);
  const to = params.toDate ? new Date(params.toDate) : new Date();
  const buckets = enumerateBuckets(from, to, granularity);

  // Scale a per-link base so filtering to a single short code shows smaller,
  // still-plausible numbers than the account-wide overview.
  const isSingleLink = (params.matchShortCode?.length ?? 0) === 1;
  const scale = isSingleLink ? 0.18 : 1;
  const perBucketBase =
    granularity === "hourly"
      ? 4
      : granularity === "daily"
        ? 46
        : granularity === "weekly"
          ? 280
          : 1150;

  const rng = seeded(buckets.length * 7 + perBucketBase);
  let totalClicks = 0;
  let uniqueClicks = 0;
  const timeSeries = buckets.map((d) => {
    const total = Math.max(
      1,
      Math.round(perBucketBase * scale * (0.5 + rng())),
    );
    const unique = Math.round(total * (0.6 + rng() * 0.2));
    totalClicks += total;
    uniqueClicks += unique;
    return {
      dateTime: d.toISOString(),
      totalClicks: total,
      uniqueClicks: unique,
    };
  });

  return { totalClicks, uniqueClicks, timeSeries };
}

export function previewShortLinkMetrics(
  field: string,
): ShortLinkMetricResult[] {
  const labels = METRIC_VALUES[field] ?? METRIC_VALUES.country;
  const rng = seeded(field.length * 131 + labels.length);
  let value = 920;
  return labels.map((label) => {
    const totalClicks = value;
    value = Math.max(18, Math.round(value * (0.5 + rng() * 0.2)));
    return { label, totalClicks };
  });
}

export function previewShortLinkQr(format?: string): {
  pngBase64?: string;
  svg?: string;
} {
  if (format === "svg") {
    return { svg: QR_SVG };
  }
  return { pngBase64: QR_PNG_BASE64 };
}
